import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import {
  EDIT_PAYMENT_FLOW_TYPE_COLUMNS,
  IN_HOUSEPART_PAYMENT,
  JODO_FLEX,
  KH_VERTICAL_ID,
  LOAN_PAYMENT_METHOD,
  PART_PAYMENT_PROVIDER_STATUS,
  PAYMENT_STATUSES,
  LOAN_STATUSES,
  PAYMENT_PLANS,
} from "constants/config";
import routes from "constants/routes";
import {
  useCancelMandate,
  useFetchOrderPaymentFlows,
  useSelectedOrderDetails,
} from "store/backend/assets";
import { Dialog, ErrorButton, PrimaryButton, Table } from "widgets";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";
import ErrorFallback from "components/ErrorFallBack/ErrorFallback";
import SwitchProvider from "components/SwitchProvider/SwitchProvider";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import styles from "./EditPaymentFlowType.module.scss";

const handleOnClickCancelMandateButton = (
  orderId,
  cancelMandate,
  openSnackbar,
) => {
  if (!orderId) {
    return;
  }
  cancelMandate(orderId, {
    onSuccess: () => {
      window.location.reload();
      openSnackbar("Successfuly cancelled mandate", "success");
    },
    onError: (error) => {
      openSnackbar(`${error.message}`, "error");
    },
  });
};

const hasPunchedLoan = (paymentDetails) => {
  if (!Array.isArray(paymentDetails)) return false;
  return paymentDetails.some((payment) => {
    if (payment?.method?.id !== LOAN_PAYMENT_METHOD) return false;
    const paymentStatusId = payment?.status?.id;
    const loanDetailsStatusId = payment?.loan_details?.status?.id;
    return (
      paymentStatusId === PAYMENT_STATUSES.CREATED ||
      paymentStatusId === PAYMENT_STATUSES.DISBURSED ||
      loanDetailsStatusId === LOAN_STATUSES.CREATED ||
      loanDetailsStatusId === LOAN_STATUSES.DISBURSED
    );
  });
};

const hasActivePartPaymentPlan = (paymentFlowsDetails) => {
  if (!Array.isArray(paymentFlowsDetails)) return false;
  const activeStatusIds = [
    PART_PAYMENT_PROVIDER_STATUS.CREATED,
    PART_PAYMENT_PROVIDER_STATUS.APPROVED,
    PART_PAYMENT_PROVIDER_STATUS.SETUP,
  ];
  return paymentFlowsDetails.some((flow) => {
    const statusId = flow?.status?.id;
    return activeStatusIds.some(
      (id) => Number(id) === Number(statusId) || id === statusId,
    );
  });
};

const isActivePartPaymentStatus = (statusId) => {
  const activeIds = [
    PART_PAYMENT_PROVIDER_STATUS.CREATED,
    PART_PAYMENT_PROVIDER_STATUS.APPROVED,
    PART_PAYMENT_PROVIDER_STATUS.SETUP,
  ];
  return activeIds.some(
    (id) => Number(id) === Number(statusId) || id === statusId,
  );
};

const getActionButton = (
  flow,
  verticalId,
  setOpenDialog,
  orderId,
  cancelMandate,
  isCancelMandateLoading,
  openSnackbar,
  showSwitchToLoan,
  onSwitchToLoanClick,
  paymentDetails,
) => {
  const isKHVertical = Number(verticalId) === Number(KH_VERTICAL_ID);
  const isActive =
    Number(flow.status?.id) === Number(PART_PAYMENT_PROVIDER_STATUS.APPROVED);
  const isInHousePartPaymentProvider = flow.name === IN_HOUSEPART_PAYMENT.name;

  const showCancelMandateButton = isActivePartPaymentStatus(flow.status?.id);

  const isActivePartPaymentRow =
    showCancelMandateButton ||
    (isKHVertical && isActive && isInHousePartPaymentProvider);
  const canShowSwitchToLoan =
    !hasPunchedLoan(paymentDetails ?? []) &&
    isActivePartPaymentRow &&
    onSwitchToLoanClick;
  const switchToLoanButton = canShowSwitchToLoan ? (
    <PrimaryButton onClick={onSwitchToLoanClick}>Switch to Loan</PrimaryButton>
  ) : null;

  if (isKHVertical && isActive && isInHousePartPaymentProvider) {
    return (
      <div className={styles.actions}>
        <PrimaryButton onClick={() => setOpenDialog(true)}>
          Switch to a different provider
        </PrimaryButton>
        {switchToLoanButton}
      </div>
    );
  }

  if (showCancelMandateButton && !isInHousePartPaymentProvider) {
    return (
      <div className={styles.actions}>
        <ErrorButton
          onClick={() =>
            handleOnClickCancelMandateButton(
              orderId,
              cancelMandate,
              openSnackbar,
            )
          }
        >
          {isCancelMandateLoading ? "Cancelling..." : "Cancel"}
        </ErrorButton>
        {switchToLoanButton}
      </div>
    );
  }

  return switchToLoanButton || "N/A";
};

const getStatusName = (flow) => {
  if (flow.id !== JODO_FLEX.id) return flow.status.name;
  const status = flow.status.id;
  if (status === PART_PAYMENT_PROVIDER_STATUS.CREATED)
    return "e-mandate pending";

  if (status === PART_PAYMENT_PROVIDER_STATUS.SETUP) return "e-mandate set";

  if (status === PART_PAYMENT_PROVIDER_STATUS.APPROVED)
    return "e-mandate approved";

  if (status === PART_PAYMENT_PROVIDER_STATUS.CANCELLED)
    return "e-mandate cancelled";

  if (status === PART_PAYMENT_PROVIDER_STATUS.EXPIRED)
    return "e-mandate expired";

  return flow.status.name;
};

export const getRows = (
  paymentFlowsDetails,
  verticalId,
  setOpenDialog,
  orderId,
  cancelMandate,
  isCancelMandateLoading,
  openSnackbar,
  showSwitchToLoan,
  onSwitchToLoanClick,
  paymentDetails,
) => {
  if (!paymentFlowsDetails || paymentFlowsDetails.length === 0) return [];

  return paymentFlowsDetails.map((flow) => ({
    provider: flow.name,
    paymentFlowStatus: getStatusName(flow),
    numberOfParts: flow.noOfParts,
    timeStamp: new Date(flow.updatedAt).toLocaleString(),
    actions: getActionButton(
      flow,
      verticalId,
      setOpenDialog,
      orderId,
      cancelMandate,
      isCancelMandateLoading,
      openSnackbar,
      showSwitchToLoan,
      onSwitchToLoanClick,
      paymentDetails,
    ),
  }));
};

const EditPaymentFlowType = () => {
  const { openSnackbar } = useSnackbarContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const verticalId = searchParams.get("verticalId");
  const [openDialog, setOpenDialog] = useState(false);
  const {
    data: paymentFlowsDetails,
    isPending: isFetchPaymentFlowsPending,
    isError: isErrorOnPaymentFlows,
    error: errorMessageOnPaymentFlows,
  } = useFetchOrderPaymentFlows(orderId);

  const { data: selectedOrderDetails } = useSelectedOrderDetails(orderId);
  const paymentDetails = selectedOrderDetails?.payment_details ?? [];

  const { mutate: cancelMandate, isPending: isCancelMandateLoading } =
    useCancelMandate();

  const hasPartPaymentFromOrder =
    Array.isArray(selectedOrderDetails?.part_payment_details) &&
    selectedOrderDetails.part_payment_details.length > 0;
  const showSwitchToLoan =
    (hasActivePartPaymentPlan(paymentFlowsDetails ?? []) ||
      hasPartPaymentFromOrder) &&
    !hasPunchedLoan(paymentDetails);

  const handleSwitchToLoanClick = () => {
    navigate(
      `${routes.paymentPreference.paymentPreference}?order_id=${orderId}&switch_to_loan=true`,
    );
  };

  if (isFetchPaymentFlowsPending) return <FullPageLoader />;

  if (isErrorOnPaymentFlows)
    return <ErrorFallback message={errorMessageOnPaymentFlows.message} />;

  return (
    <div>
      <div className={styles.header}>Edit Payment Flow Type</div>
      {paymentFlowsDetails.length === 0 ? (
        <>
          {showSwitchToLoan && (
            <div className={styles.switchToLoanRow}>
              <PrimaryButton onClick={handleSwitchToLoanClick}>
                Switch to Loan
              </PrimaryButton>
            </div>
          )}
          <div className={styles.noPaymentFlowsMessage}>
            No payment flows available for this order.
          </div>
        </>
      ) : (
        <div className={styles.paymentFlowsTable}>
          <Table
            columns={EDIT_PAYMENT_FLOW_TYPE_COLUMNS}
            rows={getRows(
              paymentFlowsDetails,
              verticalId,
              setOpenDialog,
              orderId,
              cancelMandate,
              isCancelMandateLoading,
              openSnackbar,
              showSwitchToLoan,
              handleSwitchToLoanClick,
              paymentDetails,
            )}
          />
        </div>
      )}
      <Dialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        height="auto"
      >
        <SwitchProvider
          onClose={() => setOpenDialog(false)}
          orderId={orderId}
        />
      </Dialog>
    </div>
  );
};
export default EditPaymentFlowType;
